import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { cn } from '@lib/utils';
import { MRL_TRL_SETTINGS } from '@utils/constants';
import { type VariantProps, cva } from 'class-variance-authority';
import { FunctionComponent, ReactNode } from 'react';

const scoreVariants = cva('flex items-center aspect-square justify-center font-bold', {
  variants: {
    size: {
      xs: '',
      small: '',
      medium: '',
      large: '',
    },
    design: {
      default: '',
      box: 'rounded-md p-1',
    },
  },
  compoundVariants: [
    {
      size: 'xs',
      design: 'box',
      class: 'w-8 h-8 text-lg',
    },
    {
      size: 'small',
      design: 'box',
      class: 'min-w-[42px] h-[42px] px-2 text-2xl',
    },
    {
      size: 'medium',
      design: 'box',
      class: 'w-16 h-16 text-[40px]',
    },
    {
      size: 'large',
      design: 'box',
      class: 'w-24 h-24 text-[64px]',
    },
    {
      size: 'xs',
      design: 'default',
      class: 'text-lg',
    },
    {
      size: 'small',
      design: 'default',
      class: 'text-[24px]',
    },
    {
      size: 'medium',
      design: 'default',
      class: 'text-[54px] leading-[54px]',
    },
  ],
  defaultVariants: {
    size: 'medium',
    design: 'box',
  },
});

type ScoreColorVariant = 'default' | 'success' | 'warning' | 'danger' | 'military';

export interface ScoreProps extends VariantProps<typeof scoreVariants> {
  className?: string;
  /**
   * The score to display.
   * If undefined or null, the score will be displayed as '?'.
   */
  score: number | undefined | null;
  /**
   * Optional main label shown next to the score.
   * Can be a string (default styled) or a custom ReactNode.
   */
  title?: ReactNode;
  /**
   * Optional secondary label shown under the title.
   * Can be a string (default styled) or a custom ReactNode.
   */
  subtitle?: ReactNode;
  colorVariant?: ScoreColorVariant;
  /**
   * Optional score type used for tooltips (e.g. TRL/MRL settings).
   * When omitted, no tooltip is shown.
   */
  type?: 'TRL' | 'MRL';
}

const getTooltipContent = (score: number | undefined | null, type?: 'TRL' | 'MRL'): ReactNode | null => {
  if (!score || score === 0 || !type) return null;

  const settingsKey = type.toLowerCase() as 'trl' | 'mrl';
  const levelData = MRL_TRL_SETTINGS[settingsKey]?.levels.find((level) => level.level === score);

  if (!levelData) return null;

  return (
    <div className="flex flex-col max-w-xs gap-1">
      <div className="font-semibold text-[#3D2EFF]">
        {type} {score}
      </div>
      <div className="font-semibold">{levelData.label}</div>
      <div className="text-sm">{levelData.description}</div>
    </div>
  );
};

type ScoreSquareProps = {
  children: ReactNode;
  className?: string;
  /**
   * The design of the score square.
   * If 'box', the score square will have a border.
   * If 'default', the score square will not have a border.
   */
  design?: 'default' | 'box';
  score: number | undefined | null;
  size?: 'xs' | 'small' | 'medium' | 'large';
};

const COLOR_MAP: Record<ScoreColorVariant, { bg: string; text: string }> = {
  danger: { bg: 'bg-red-50', text: 'text-red-700' },
  default: { bg: 'bg-[#3D2EFF1A]', text: 'text-gray-800' },
  success: { bg: 'bg-green-50', text: 'text-green-700' },
  warning: { bg: 'bg-amber-50', text: 'text-amber-700' },
  military: { bg: 'bg-teal-500/10', text: 'text-gray-800' },
};

const ScoreSquare: FunctionComponent<ScoreSquareProps & { colorVariant?: ScoreColorVariant }> = (props) => {
  const { children, className, colorVariant = 'default', design = 'box', score, size } = props;

  const borderStyle = design === 'box' && !score ? 'border border-dashed border-gray-300' : '';
  const backgroundStyle = design === 'box' && score ? COLOR_MAP[colorVariant].bg : '';

  return (
    <div
      className={cn(
        scoreVariants({ size, design }),
        borderStyle,
        backgroundStyle,
        size === 'large' && 'font-semibold text-[#1F1C1A]',
        className,
      )}
    >
      {children}
    </div>
  );
};

type ScoreWithTooltipProps = {
  children: ReactNode;
  tooltipContent: ReactNode | null;
};

const ScoreWithTooltip: FunctionComponent<ScoreWithTooltipProps> = (props) => {
  const { children, tooltipContent } = props;

  if (!tooltipContent) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">{children}</div>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const Score: FunctionComponent<ScoreProps> = (props) => {
  const { className, colorVariant = 'default', design = 'box', score, size, subtitle, title, type } = props;

  const tooltipContent = getTooltipContent(score, type);
  const textColor = score ? COLOR_MAP[colorVariant].text : 'text-gray-400';

  const scoreSquareProps: Omit<ScoreSquareProps, 'children'> & { colorVariant: ScoreColorVariant } = {
    className,
    colorVariant,
    design: design || undefined,
    score,
    size: size || undefined,
  };

  const hasTitleOrSubtitle = Boolean(title) || Boolean(subtitle);

  if (!hasTitleOrSubtitle) {
    return (
      <ScoreWithTooltip tooltipContent={tooltipContent}>
        <ScoreSquare {...scoreSquareProps}>
          <span className={textColor}>{score ?? '?'}</span>
        </ScoreSquare>
      </ScoreWithTooltip>
    );
  }

  const renderTitle = () => {
    if (!title) return null;
    if (typeof title === 'string') {
      return (
        <span className={cn('font-semibold text-[#3D2EFF]', size === 'large' ? 'text-[16px]' : 'text-[12px]')}>
          {title}
        </span>
      );
    }
    return title;
  };

  const renderSubtitle = () => {
    if (!subtitle) return null;
    if (typeof subtitle === 'string') {
      return (
        <span className={cn('font-light text-[#1F1C1AB2]', size === 'large' ? 'text-[14px]' : 'text-[12px]')}>
          {subtitle}
        </span>
      );
    }
    return subtitle;
  };

  return (
    <div className={cn('flex items-start', size === 'large' ? 'gap-3' : 'gap-2')}>
      <ScoreWithTooltip tooltipContent={tooltipContent}>
        <ScoreSquare {...scoreSquareProps}>
          <span className={textColor}>{score ?? '?'}</span>
        </ScoreSquare>
      </ScoreWithTooltip>
      <div className={cn('flex flex-col items-start', size !== 'small' && size !== 'xs' ? 'pt-2' : '')}>
        {renderTitle()}
        {renderSubtitle()}
      </div>
    </div>
  );
};
